/**
 * Camada 1 · os dois controles que TODO sistema tem, no MESMO canto.
 *
 * FRAME SUPERIOR, A DIREITA, SEMPRE JUNTOS, idioma primeiro e tema em seguida, em
 * qualquer largura, inclusive no celular. Quem troca de produto encontra os dois no
 * mesmo lugar, e e isso que faz a familia de sistemas parecer uma familia.
 *
 * IDIOMA E DOIS BOTOES, e nao um menu suspenso: sao duas opcoes, e um select em 390px
 * abre a lista nativa do sistema operacional, ou seja, mais gesto para menos escolha.
 *
 * `aria-pressed` em todos, porque cor sozinha nao chega a quem usa leitor de tela.
 */

import { useEffect, useState } from 'react';
// `IDIOMAS_ATIVOS`, e nao `IDIOMAS`: neste projeto so o portugues esta ligado (decisao do fundador
// em 19/09/2026), e o controle de idioma so aparece quando ha mais de um. Divergencia protegida.
import { IDIOMAS_ATIVOS, definirIdioma, idiomaAtual, t, type Idioma } from '@complexity/i18n';
import { alternarTema, iniciarTema, temaEfetivo, type Tema } from '@complexity/theme';
// Import RELATIVO porque esta peca passou a morar dentro do proprio @complexity/ui em
// 31/08/2026. Package que importa a si mesmo pelo nome cria ciclo de resolucao.
import { IconeSol, IconeLua } from './Icones.js';

export function ControlesIdiomaTema() {
  const [idioma, setIdioma] = useState<Idioma>(() => idiomaAtual());
  const [tema, setTema] = useState<Tema>('light');

  useEffect(() => {
    // iniciarTema aplica a preferencia no documento e devolve a funcao que para a escuta
    // da preferencia do sistema. Sem chamar o retorno, a escuta vaza a cada montagem.
    const parar = iniciarTema();
    setTema(temaEfetivo());
    return parar;
  }, []);

  const trocarIdioma = (novo: Idioma) => {
    definirIdioma(novo);
    setIdioma(novo);
    // A troca de idioma precisa alcancar a TELA INTEIRA, e nao so este componente.
    // Metade da interface trocando e pior que seletor nenhum: a pessoa passa a
    // desconfiar do que leu. O evento avisa quem estiver ouvindo para redesenhar.
    window.dispatchEvent(new CustomEvent('cx:idioma', { detail: novo }));
  };

  return (
    <div className="flex items-center gap-cx-2">
      {/* IDIOMA · primeiro, como manda o padrao da familia de produtos. So existe com mais de
          um idioma ligado: um seletor de uma opcao so e um botao que nao faz nada. */}
      {IDIOMAS_ATIVOS.length > 1 && (
      <div
        role="group"
        aria-label={t('Idioma')}
        className="flex overflow-hidden rounded-cx-md border border-borda"
      >
        {IDIOMAS_ATIVOS.map((cod) => (
          <button
            key={cod}
            type="button"
            onClick={() => trocarIdioma(cod)}
            aria-pressed={idioma === cod}
            /* `min-w-toque` ALEM de `min-h-toque`, e os dois sao obrigatorios.
               DEFEITO MEDIDO EM 30/08/2026, na varredura dos 5 apps que ele pediu como
               diretriz primaria: estes botoes tinham 40x44 em TODAS as larguras, dos 320px
               ao monitor de 1920. `px-cx-3` sobre duas letras nao chega aos 44, e o
               `min-h-toque` sozinho passava em qualquer teste que so olhasse a altura.
               Alvo de 44 de altura com 40 de largura continua sendo alvo que o dedo erra. */
            className={[
              'flex min-h-toque min-w-toque items-center justify-center px-cx-3 text-cx-xs font-medium uppercase',
              // O IDIOMA ATIVO USA 'marca-suave', E NAO O FUNDO CHEIO. Decisao dele em
              // 06/09/2026: *"o azul em destaque do menu e da PT/EN era pra ficar o mais
              // claro em ambos (azul bem mais discreto)"*, apontando o menu lateral como
              // referencia.
              //
              // POR QUE ISSO E O PADRAO E NAO PREFERENCIA: sao dois controles de CANTO, num
              // lugar onde a pessoa nao esta olhando. Fundo cheio da marca ali compete com
              // a acao principal da tela, e a marca perde o significado quando pinta tambem
              // o que e so estado. O ativo se destaca por CONTRASTE DE TEXTO sobre um fundo
              // discreto, que e a mesma escolha do item ativo do rail.
              idioma === cod ? 'bg-marca-suave font-semibold text-marca' : 'bg-superficie text-texto-suave',
            ].join(' ')}
          >
            {cod}
          </button>
        ))}
      </div>
      )}

      {/* TEMA · em seguida, sempre colado no de idioma */}
      <button
        type="button"
        onClick={() => setTema(alternarTema())}
        aria-pressed={tema === 'dark'}
        // O rotulo diz o que VAI acontecer, e nao o estado atual: "tema escuro" num
        // botao ja escuro deixa quem usa leitor de tela sem saber o que o clique faz.
        aria-label={tema === 'dark' ? t('Mudar para tema claro') : t('Mudar para tema escuro')}
        className="min-h-toque min-w-toque rounded-cx-md border border-borda bg-superficie text-texto-suave hover:bg-superficie-afundada"
      >
        {/* DESENHO, e nao caractere: o simbolo de lua nao existe em toda fonte, e onde
            falta o navegador troca por um retangulo vazio. */}
        {tema === 'dark' ? (
          <IconeSol className="mx-auto h-5 w-5" />
        ) : (
          <IconeLua className="mx-auto h-5 w-5" />
        )}
      </button>
    </div>
  );
}
